import React, { useState } from 'react';
import styled from 'styled-components';
import Images from 'images';

const platform = process.env.REACT_APP_PLATFORM;

const getLogo = () => {
  if (platform === 'tickethoss') {
    return { src: Images.ticketHossCollapsedLogo, compact: true };
  }
  if (platform === 'pitpay') {
    return { src: Images.pitpayCollapsedLogo, compact: true };
  }
  if (platform === 'kartpass') return { src: Images.logoKart, compact: false };
  return { src: Images.logoHorizontalWhite, compact: false };
};

const PageContainer = styled.div`
  min-height: 100vh;
  min-height: 100dvh;
  background: #0f172a;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  padding: 32px;
  box-sizing: border-box;
  overflow-x: hidden;
  font-family: ${(props) =>
    props.theme?.fonts?.primary ||
    '-apple-system, BlinkMacSystemFont, "Segoe UI", "Helvetica Neue", Arial, sans-serif'};

  @media (max-width: 900px) {
    justify-content: ${(props) =>
      props.$keyboardActive ? 'flex-start' : 'center'};
    padding: ${(props) =>
      props.$keyboardActive
        ? `max(12px, env(safe-area-inset-top)) 12px calc(24px + env(safe-area-inset-bottom))`
        : '20px 12px 24px'};
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
  }

  @media (max-width: 760px) {
    background: #080e1c;
  }

  @media (max-width: 900px) and (max-height: 740px) {
    justify-content: flex-start;
  }
`;

const SplitCard = styled.div`
  width: min(100%, 840px);
  max-width: 840px;
  min-height: 440px;
  display: flex;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.5);
  box-sizing: border-box;

  @media (max-width: 900px) {
    flex-direction: column;
  }

  @media (max-width: 760px) {
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.6);
    border: 1px solid rgba(255, 255, 255, 0.1);
  }
`;

const BrandSide = styled.div`
  flex: 1;
  background: linear-gradient(160deg, #fa4616 0%, #d63a10 100%);
  padding: 48px 36px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  text-align: center;

  @media (max-width: 900px) {
    padding: 32px 24px;
  }
`;

const BrandLogo = styled.img`
  width: 216px;
  max-width: 100%;
  height: auto;

  ${(props) =>
    props.$compact
      ? `
    @media (max-width: 900px) {
      width: 120px;
    }
  `
      : ''}
`;

const BrandDivider = styled.div`
  width: 40px;
  height: 3px;
  background: rgba(255, 255, 255, 0.4);
  border-radius: 2px;
  margin: 20px 0;
`;

const PromoText = styled.div`
  font-family: ${(props) =>
    props.theme?.fonts?.primary ||
    '-apple-system, BlinkMacSystemFont, "Segoe UI", "Helvetica Neue", Arial, sans-serif'};
  font-size: 42px;
  line-height: 0.9;
  font-weight: 700;
  color: #fff;
  text-transform: uppercase;

  @media (max-width: 768px) {
    font-size: 37px;
  }
`;

const Tagline = styled.p`
  color: rgba(255, 255, 255, 0.85);
  font-size: 14px;
  line-height: 1.5;
  margin-top: 12px;

  @media (max-width: 900px) {
    font-size: 15px;
  }
`;

const FormSide = styled.div`
  flex: 1;
  background: #1e293b;
  padding: 48px 36px;
  display: flex;
  flex-direction: column;
  justify-content: center;

  @media (max-width: 900px) {
    padding: 32px 24px;
  }

  @media (max-width: 760px) {
    background: #141e30;
  }
`;

const Title = styled.h1`
  color: #fff;
  font-size: 22px;
  margin: 0 0 6px;
  font-weight: 700;
  text-align: center;

  @media (max-width: 900px) {
    font-size: 23px;
  }
`;

const Subtitle = styled.p`
  color: #94a3b8;
  font-size: 14px;
  line-height: 1.5;
  margin: 0 0 28px;
  text-align: center;

  @media (max-width: 900px) {
    font-size: 15px;
  }

  @media (max-width: 760px) {
    color: #b4c4d6;
  }
`;

const isInputElement = (element) =>
  element && ['INPUT', 'TEXTAREA', 'SELECT'].includes(element.tagName);

const SplitScreenLayout = ({ title, subtitle, children }) => {
  const [keyboardActive, setKeyboardActive] = useState(false);
  const logo = getLogo();

  return (
    <PageContainer
      $keyboardActive={keyboardActive}
      onFocusCapture={(event) => {
        if (typeof window === 'undefined') return;
        if (!window.matchMedia('(max-width: 900px)').matches) return;
        if (isInputElement(event.target)) {
          setKeyboardActive(true);
        }
      }}
      onBlurCapture={(event) => {
        if (typeof window === 'undefined') return;
        window.setTimeout(() => {
          const active = document.activeElement;
          const activeInside = event.currentTarget.contains(active);
          setKeyboardActive(activeInside && isInputElement(active));
        }, 0);
      }}
    >
      <SplitCard>
        <BrandSide>
          <BrandLogo src={logo.src} $compact={logo.compact} alt="Logo" />
          <BrandDivider />
          <PromoText>
            Promoter
            <br />
            Sign Up
          </PromoText>
          <Tagline>
            Create your account to start
            <br />
            selling tickets today
          </Tagline>
        </BrandSide>
        <FormSide>
          <Title>{title}</Title>
          {subtitle ? <Subtitle>{subtitle}</Subtitle> : null}
          {children}
        </FormSide>
      </SplitCard>
    </PageContainer>
  );
};

export default SplitScreenLayout;
